import { ThemeContext } from '@f_context/Theme_context';
import { Box, FormControl, MenuItem, Select, TextField, TextFieldProps } from '@mui/material';
import React, { useContext, useEffect, useState } from 'react';
import { MigrationValidator } from './MigrationValidation';

interface PhoneInputProps extends Omit<TextFieldProps, 'onChange'> {
    value: string;
    onChange: (value: string) => void;
    error?: boolean;
    helperText?: string;
}

type Country = 'RU' | 'AM' | 'KZ';

const COUNTRIES = [
    { code: 'RU', name: 'Россия', prefix: '+7', mask: '(###) ###-##-##' },
    { code: 'AM', name: 'Армения', prefix: '+374', mask: '## ###-###' },
    { code: 'KZ', name: 'Казахстан', prefix: '+7', mask: '### ###-##-##' },
];

export const PhoneInput: React.FC<PhoneInputProps> = ({ value, onChange, error, helperText, ...props }) => {
    const { CustomSelect, colors } = useContext(ThemeContext);
    const [selectedCountry, setSelectedCountry] = useState<Country>('RU');
    const [phoneDigits, setPhoneDigits] = useState('');
    const [displayValue, setDisplayValue] = useState('');

    useEffect(() => {
        if (value && value.trim() !== '') {
            const country = MigrationValidator.getPhoneCountry(value);
            if (country) {
                setSelectedCountry(country);
                const cleanValue = value.replace(/\D/g, '');
                if (country === 'AM') {
                    setPhoneDigits(cleanValue.substring(3));
                } else {
                    setPhoneDigits(cleanValue.substring(1));
                }
            }
        } else {
            setPhoneDigits('');
            setDisplayValue('');
        }
    }, [value]);

    useEffect(() => {
        formatPhoneNumber();
    }, [selectedCountry, phoneDigits]);

    const formatPhoneNumber = () => {
        const country = COUNTRIES.find((c) => c.code === selectedCountry);
        if (!country) return;

        let formatted = country.prefix;

        if (phoneDigits.length > 0) {
            if (selectedCountry === 'AM') {
                const code = phoneDigits.substring(0, 2);
                const part1 = phoneDigits.substring(2, 5);
                const part2 = phoneDigits.substring(5, 8);

                formatted += ` ${code}`;
                if (part1) formatted += ` ${part1}`;
                if (part2) formatted += `-${part2}`;
            } else {
                const areaCode = phoneDigits.substring(0, 3);
                const part1 = phoneDigits.substring(3, 6);
                const part2 = phoneDigits.substring(6, 8);
                const part3 = phoneDigits.substring(8, 10);

                if (selectedCountry === 'RU') {
                    formatted += ` (${areaCode}`;
                    if (part1) formatted += `) ${part1}`;
                    if (part2) formatted += `-${part2}`;
                    if (part3) formatted += `-${part3}`;
                } else {
                    formatted += ` ${areaCode}`;
                    if (part1) formatted += ` ${part1}`;
                    if (part2) formatted += `-${part2}`;
                    if (part3) formatted += `-${part3}`;
                }
            }
        }

        setDisplayValue(formatted);
        onChange(formatted);
    };

    const handleCountryChange = (countryCode: Country) => {
        setSelectedCountry(countryCode);
        setPhoneDigits('');
    };

    const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const inputValue = e.target.value;

        const digits = inputValue.replace(/\D/g, '');

        let cleanDigits = digits;
        if (selectedCountry === 'AM' && digits.startsWith('374')) {
            cleanDigits = digits.substring(3);
        } else if ((selectedCountry === 'RU' || selectedCountry === 'KZ') && digits.startsWith('7')) {
            cleanDigits = digits.substring(1);
        }

        const maxDigits = selectedCountry === 'AM' ? 8 : 10;
        const limitedDigits = cleanDigits.substring(0, maxDigits);

        setPhoneDigits(limitedDigits);
    };

    const getPlaceholder = () => {
        const country = COUNTRIES.find((c) => c.code === selectedCountry);
        if (!country) return '';

        switch (selectedCountry) {
            case 'AM':
                return '+374 10 123-456';
            case 'RU':
                return '+7 (999) 123-45-67';
            case 'KZ':
                return '+7 701 123-45-67';
            default:
                return '';
        }
    };

    return (
        <Box sx={{ display: 'flex', gap: 1, maxWidth: 'fit-content' }}>
            <FormControl sx={{ minWidth: 160 }}>
                <Select
                    input={<CustomSelect />}
                    value={selectedCountry}
                    label="Страна"
                    sx={{
                        '& .MuiInputBase-input': {
                            padding: '7px 26px 6px 12px',
                        },
                    }}
                    MenuProps={{
                        sx: {
                            '&& .MuiMenuItem-root': {
                                fontSize: 14,
                                fontFamily: 'Inter',
                                color: colors.omicron,
                                '&:hover': {
                                    background: colors.beta,
                                },
                            },
                        },
                    }}
                    onChange={(e) => handleCountryChange(e.target.value as Country)}
                    size="small">
                    {COUNTRIES.map((country) => (
                        <MenuItem key={country.code} value={country.code}>
                            {country.name}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
            <TextField
                {...props}
                value={displayValue}
                onChange={handlePhoneChange}
                placeholder={getPlaceholder()}
                error={error}
                helperText={helperText}
                sx={{ maxWidth: '236px' }}
                inputProps={{
                    ...props.inputProps,
                    maxLength: 20,
                }}
            />
        </Box>
    );
};
